import uiI18n from "../i18n";
import React, { useEffect, useRef, useState } from "react";
import { AiFillEyeInvisible, AiFillEye } from "react-icons/ai";
import {
  FiUser,
  FiMail,
  FiLock,
  FiCheckCircle,
  FiHome,
  FiShield,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import OAuth from "../components/OAuth";
import { getFirestore, doc, setDoc } from "firebase/firestore";

import {
  updateProfile,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";

import { auth } from "../firebase";
import { toast } from "react-toastify";
import Photo from "../assets/Pictures/linaup.png";
import { useTranslation } from "react-i18next";

export default function SignUp() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const formRef = useRef();
  const verificationTimer = useRef();

  useEffect(() => {
    return () => clearInterval(verificationTimer.current);
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConf, setShowPasswordConf] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    passwordConf: "",
  });

  const { name, email, password, passwordConf } = formData;

  function onChange(e) {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  }

  const onSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6 || passwordConf.length < 6) {
      toast.error(uiI18n.t("ui.at_least_6_characters"));
      return;
    }

    if (password !== passwordConf) {
      toast.error(uiI18n.t("ui.passwords_do_not_match"));
      return;
    }

    try {
      setLoading(true);

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;

      await updateProfile(user, {
        displayName: name.trim(),
      });

      await setDoc(doc(getFirestore(), "users", user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: name.trim(),
        createdAt: new Date(),
      });

      await sendEmailVerification(user);

      toast.info(
        uiI18n.t(
          "ui.a_verification_email_has_been_sent_please_verify_your_email_before_continuing"
        )
      );

      formRef.current?.reset();

      clearInterval(verificationTimer.current);

      verificationTimer.current = setInterval(async () => {
        try {
          await user.reload();

          if (user.emailVerified) {
            clearInterval(verificationTimer.current);

            toast.success(uiI18n.t("ui.email_verified"));
            navigate("/");
          }
        } catch {
          clearInterval(verificationTimer.current);
        }
      }, 3000);

    } catch (err) {

      if (err.code === "auth/invalid-email") {
        toast.error(uiI18n.t("ui.invalid_email"));
      } else if (err.code === "auth/email-already-in-use") {
        toast.error(uiI18n.t("ui.email_already_in_use"));
      } else {
        toast.error(uiI18n.t("ui.registration_failed"));
      }

      console.error(err);

    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-[#f6f8f7]">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">

        <div className="relative min-h-[720px] overflow-hidden rounded-[28px] shadow-xl">

          {/* BACKGROUND */}
          <img
            src={Photo}
            alt={uiI18n.t("ui.illustration")}
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/75 to-black/10" />

          <div className="relative z-10 grid min-h-[720px] lg:grid-cols-2">

            {/* PRESENTATION */}
            <div className="hidden lg:flex flex-col justify-center px-14 xl:px-20">

              <span className="inline-flex w-fit rounded-full bg-green-100 px-4 py-2 text-sm font-semibold text-green-800 mb-5">
                LINA IMMO
              </span>

              <h1 className="text-5xl font-extrabold leading-tight text-slate-900">
                Rejoignez
                <span className="block text-green-700">
                  Lina Immo
                </span>
              </h1>

              <p className="mt-6 max-w-md text-lg leading-8 text-slate-600">
                Créez votre espace personnel et gérez simplement
                votre projet immobilier.
              </p>

              <div className="mt-9 space-y-4">

                <Feature
                  icon={<FiUser />}
                  title="Créez votre profil en quelques clics"
                />

                <Feature
                  icon={<FiHome />}
                  title="Publiez et gérez vos annonces"
                />

                <Feature
                  icon={<FiShield />}
                  title="Un espace personnel sécurisé"
                />

                <Feature
                  icon={<FiCheckCircle />}
                  title="Suivez vos recherches et vos opportunités"
                />

              </div>

              <p className="mt-12 text-xl italic font-medium text-green-800">
                Votre projet commence ici.
              </p>

            </div>

            {/* FORM */}
            <div className="flex items-center justify-center px-4 py-10 sm:px-8">

              <div className="w-full max-w-md rounded-[26px] border border-white bg-white/95 p-7 shadow-2xl backdrop-blur-md sm:p-9">

                <div className="mb-7">

                  <div className="flex items-center justify-between">

                    <div>

                      <p className="text-sm font-semibold uppercase tracking-wider text-green-700">
                        Lina Immo
                      </p>

                      <h2 className="mt-1 text-3xl font-bold text-slate-900">
                        {t("sign_up")}
                      </h2>

                    </div>

                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-50 text-2xl text-green-700">
                      <FiUser />
                    </div>

                  </div>

                  <p className="mt-3 text-sm text-slate-500">
                    Créez votre compte gratuitement.
                  </p>

                </div>

                <form onSubmit={onSubmit} ref={formRef}>

                  {/* NAME */}
                  <FormInput
                    icon={<FiUser />}
                    type="text"
                    id="name"
                    value={name}
                    onChange={onChange}
                    placeholder={t("name")}
                    autoComplete="name"
                  />

                  {/* EMAIL */}
                  <FormInput
                    icon={<FiMail />}
                    type="email"
                    id="email"
                    value={email}
                    onChange={onChange}
                    placeholder={t("email_placeholder")}
                    autoComplete="email"
                  />

                  {/* PASSWORD */}
                  <PasswordInput
                    id="password"
                    value={password}
                    onChange={onChange}
                    placeholder={t("password_placeholder")}
                    show={showPassword}
                    setShow={setShowPassword}
                    autoComplete="new-password"
                  />

                  {/* CONFIRM */}
                  <PasswordInput
                    id="passwordConf"
                    value={passwordConf}
                    onChange={onChange}
                    placeholder={t("confirmation_password")}
                    show={showPasswordConf}
                    setShow={setShowPasswordConf}
                    autoComplete="new-password"
                  />

                  <p className="mb-5 text-xs text-slate-500">
                    Le mot de passe doit contenir au minimum 6 caractères.
                  </p>

                  {/* SUBMIT */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-xl bg-green-700 px-6 py-3.5 font-semibold text-white shadow-lg shadow-green-700/20 transition hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading
                      ? uiI18n.t("ui.sending")
                      : t("sign_up")}
                  </button>

                  {/* OR */}
                  <div className="flex items-center my-6 before:flex-1 before:border-t before:border-slate-200 after:flex-1 after:border-t after:border-slate-200">

                    <span className="mx-4 text-xs font-semibold uppercase text-slate-400">
                      {t("or")}
                    </span>

                  </div>

                  <OAuth />

                  <p className="mt-7 text-center text-sm text-slate-600">

                    Vous avez déjà un compte ?{" "}

                    <Link
                      to="/signin"
                      className="font-bold text-green-700 hover:text-green-800"
                    >
                      {t("sign_in")}
                    </Link>

                  </p>

                </form>
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM TRUST BAR */}
        {/* <div className="grid grid-cols-2 gap-4 py-8 md:grid-cols-4">

          <TrustItem
            title="Simple"
            subtitle="Inscription rapide"
          />

          <TrustItem
            title="Sécurisé"
            subtitle="Vos données protégées"
          />

          <TrustItem
            title="Gratuit"
            subtitle="Création de compte"
          />

          <TrustItem
            title="Accessible"
            subtitle="Web et mobile"
          />

        </div> */}

      </div>
    </section>
  );
}

function FormInput({
  icon,
  type,
  id,
  value,
  onChange,
  placeholder,
  autoComplete,
}) {
  return (
    <div className="relative mb-4">

      <span className="absolute start-4 top-1/2 -translate-y-1/2 text-slate-400">
        {icon}
      </span>

      <input
        required
        type={type}
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3.5 ps-11 pe-4 text-slate-800 outline-none transition focus:border-green-600 focus:bg-white focus:ring-4 focus:ring-green-100"
      />

    </div>
  );
}

function PasswordInput({
  id,
  value,
  onChange,
  placeholder,
  show,
  setShow,
  autoComplete,
}) {
  return (
    <div className="relative mb-4">

      <FiLock className="absolute start-4 top-1/2 -translate-y-1/2 text-slate-400" />

      <input
        required
        type={show ? "text" : "password"}
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3.5 ps-11 pe-12 text-slate-800 outline-none transition focus:border-green-600 focus:bg-white focus:ring-4 focus:ring-green-100"
      />

      <button
        type="button"
        aria-label={
          show
            ? "Masquer le mot de passe"
            : "Afficher le mot de passe"
        }
        onClick={() => setShow((prev) => !prev)}
        className="absolute end-4 top-1/2 -translate-y-1/2 text-xl text-slate-500 hover:text-green-700"
      >
        {show ? <AiFillEyeInvisible /> : <AiFillEye />}
      </button>

    </div>
  );
}

function Feature({ icon, title }) {
  return (
    <div className="flex items-center gap-3 text-slate-700">

      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-lg text-green-700">
        {icon}
      </div>

      <span className="font-medium">
        {title}
      </span>

    </div>
  );
}

function TrustItem({ title, subtitle }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-4 text-center">

      <p className="font-bold text-green-800">
        {title}
      </p>

      <p className="mt-1 text-xs text-slate-500">
        {subtitle}
      </p>

    </div>
  );
}